import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import moment from 'moment';
import { MdClose, MdContentCopy, MdCheck, MdVisibility } from 'react-icons/md';
import { getAllCohorts, createCohortV3, updateCohortV3 } from '../../../services/apiService';
import { COURSE_TYPE, COURSE_TYPE_NAMES_MAPPING } from '../../../constants';
import { styledDefaultColDef } from '../../../extras/cellRenderers';
import LoadingCircleIcon from '../../../icons/LoadingCircle';

const STATUS_CONFIG = {
  Created:   { bg: '#D1FAE5', color: '#065F46', border: '#A7F3D0' },
  Scheduled: { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' },
  Failed:    { bg: '#FEE2E2', color: '#991B1B', border: '#FECACA' },
};

function getStatus(cohort) {
  if (cohort.status) return cohort.status;
  if (cohort.scheduleDate) return 'Scheduled';
  return 'Created';
}

const initialForm = {
  name: '',
  courseType: COURSE_TYPE.DATA_SCIENCE,
  startDate: null,
  classStartDate: null,
  scheduleDate: null,
};

export default function AddCohortPage() {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [isView, setIsView] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);

  const fetchCohorts = async () => {
    setLoading(true);
    try {
      const data = await getAllCohorts();
      setCohorts(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCohorts();
  }, []);

  const openEdit = useCallback((cohort) => {
    setEditTarget(cohort);
    setIsView(false);
    setForm({
      name: cohort.name || '',
      courseType: cohort.courseType || COURSE_TYPE.DATA_SCIENCE,
      startDate: cohort.startDate ? new Date(cohort.startDate) : null,
      classStartDate: cohort.classStartDate ? new Date(cohort.classStartDate) : null,
      scheduleDate: cohort.scheduleDate ? new Date(cohort.scheduleDate) : null,
    });
    setModalOpen(true);
  }, []);

  const openView = useCallback((cohort) => {
    setEditTarget(cohort);
    setIsView(true);
    setForm({
      name: cohort.name || '',
      courseType: cohort.courseType || COURSE_TYPE.DATA_SCIENCE,
      startDate: cohort.startDate ? new Date(cohort.startDate) : null,
      classStartDate: cohort.classStartDate ? new Date(cohort.classStartDate) : null,
      scheduleDate: cohort.scheduleDate ? new Date(cohort.scheduleDate) : null,
    });
    setModalOpen(true);
  }, []);

  const handleClose = () => {
    setModalOpen(false);
    setEditTarget(null);
    setIsView(false);
    setForm(initialForm);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.courseType) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        courseType: form.courseType,
        startDate: form.startDate ? moment(form.startDate).format('YYYY-MM-DD') : undefined,
        classStartDate: form.classStartDate ? moment(form.classStartDate).format('YYYY-MM-DD') : undefined,
        scheduleDate: form.scheduleDate ? moment(form.scheduleDate).format('YYYY-MM-DD') : undefined,
      };
      if (editTarget) {
        await updateCohortV3({ ...payload, name: editTarget.name });
      } else {
        await createCohortV3(payload);
      }
      handleClose();
      fetchCohorts();
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(
      editTarget
        ? {
            name: editTarget.name || '',
            courseType: editTarget.courseType || COURSE_TYPE.DATA_SCIENCE,
            startDate: editTarget.startDate ? new Date(editTarget.startDate) : null,
            classStartDate: editTarget.classStartDate ? new Date(editTarget.classStartDate) : null,
            scheduleDate: editTarget.scheduleDate ? new Date(editTarget.scheduleDate) : null,
          }
        : initialForm
    );
  };

  const columnDefs = [
    {
      headerName: 'Cohort Name',
      field: 'name',
      filter: 'agTextColumnFilter',
      filterParams: { filterOptions: ['contains'], debounceMs: 200 },
      flex: 2,
      cellRenderer: (params) => {
        const handleCopy = (e) => {
          const btn = e.currentTarget;
          const copyIcon = btn.querySelector('.copy-icon');
          const checkIcon = btn.querySelector('.check-icon');
          navigator.clipboard.writeText(params.value || '').catch(() => {
            const el = document.createElement('textarea');
            el.value = params.value || '';
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
          });
          if (copyIcon) copyIcon.style.display = 'none';
          if (checkIcon) checkIcon.style.display = 'inline-flex';
          setTimeout(() => {
            if (copyIcon) copyIcon.style.display = 'inline-flex';
            if (checkIcon) checkIcon.style.display = 'none';
          }, 1500);
        };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
            <span style={{ fontSize: 13, color: '#111827' }}>{params.value}</span>
            <button
              onClick={handleCopy}
              title="Copy cohort name"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px 4px',
                display: 'inline-flex',
                alignItems: 'center',
                color: '#9CA3AF',
                borderRadius: 3,
                flexShrink: 0,
              }}
            >
              <span className="copy-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <MdContentCopy size={13} />
              </span>
              <span className="check-icon" style={{ display: 'none', alignItems: 'center', color: '#10B981' }}>
                <MdCheck size={13} />
              </span>
            </button>
          </div>
        );
      },
    },
    {
      headerName: 'Start Date',
      field: 'startDate',
      filter: 'agDateColumnFilter',
      flex: 1,
      valueFormatter: (p) =>
        p.value ? moment(p.value).format('DD MMM YYYY') : '—',
    },
    {
      headerName: 'Class Start Date',
      field: 'classStartDate',
      filter: 'agDateColumnFilter',
      flex: 1,
      valueFormatter: (p) =>
        p.value ? moment(p.value).format('DD MMM YYYY') : '—',
    },
    {
      headerName: 'Course Type',
      field: 'courseType',
      filter: 'agTextColumnFilter',
      flex: 1,
    },
    {
      headerName: 'Status',
      flex: 1,
      sortable: false,
      filter: false,
      cellRenderer: (params) => {
        const status = getStatus(params.data);
        const s = STATUS_CONFIG[status] || STATUS_CONFIG.Created;
        return (
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '2px 10px',
                borderRadius: '9999px',
                fontSize: 11.5,
                fontWeight: 600,
                backgroundColor: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                lineHeight: 1.6,
              }}
            >
              {status}
            </span>
          </div>
        );
      },
    },
    {
      headerName: 'Action',
      flex: 1,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (params) => {
        const status = getStatus(params.data);
        const canEdit = status === 'Scheduled' || status === 'Failed';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
            <button
              onClick={() => openView(params.data)}
              title="View cohort"
              style={{
                background: 'none',
                border: '1px solid #E5E7EB',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                color: '#6B7280',
              }}
            >
              <MdVisibility size={14} />
            </button>
            {canEdit && (
              <button
                onClick={() => openEdit(params.data)}
                style={{
                  background: 'none',
                  border: '1px solid #E5E7EB',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#4F46E5',
                  padding: '4px 10px',
                }}
              >
                Edit
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const defaultColDef = {
    ...styledDefaultColDef,
    filter: true,
    resizable: true,
    sortable: true,
  };

  const modalTitle = isView ? 'View Cohort' : editTarget ? 'Edit Cohort' : 'Create Cohort';
  const modalSubtitle = isView
    ? 'Cohort details (read-only)'
    : editTarget
    ? 'Update cohort dates and settings'
    : 'Fill in both sections to create a new cohort';

  return (
    <div>
      {/* Page header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>
            Add Cohort
          </h1>
          <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4, marginBottom: 0 }}>
            Fill in the details below to create a cohort
          </p>
        </div>
      </div>

      {/* AG-Grid table */}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          border: '1px solid rgba(0,0,0,0.07)',
          boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '14px 20px',
            borderBottom: '1px solid #F3F4F6',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Cohorts</span>
          {!loading && (
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: '#6B7280',
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                borderRadius: 9999,
                padding: '2px 10px',
              }}
            >
              {cohorts.length} records
            </span>
          )}
        </div>

        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '64px 0',
            }}
          >
            <LoadingCircleIcon iconStyle="w-8 h-8 mr-2" />
            <span style={{ fontSize: 13, color: '#6B7280', marginLeft: 8 }}>Loading cohorts…</span>
          </div>
        ) : (
          <div className="ag-theme-alpine" style={{ height: '70vh', width: '100%' }}>
            <AgGridReact
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowData={cohorts}
              pagination={true}
              paginationPageSize={10}
              tooltipShowDelay={0}
              tooltipHideDelay={2000}
              onGridReady={(params) => {
                params.api.sizeColumnsToFit();
              }}
              onGridSizeChanged={(params) => {
                params.api.sizeColumnsToFit();
              }}
            />
          </div>
        )}
      </div>

      {/* Create / Edit / View Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { borderRadius: 16, overflow: 'hidden', maxWidth: 700 },
        }}
      >
        <DialogContent style={{ padding: 0 }}>
          {/* Modal header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '18px 24px',
              borderBottom: '1px solid #F3F4F6',
            }}
          >
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>
                {modalTitle}
              </h2>
              <p style={{ fontSize: 12, color: '#9CA3AF', margin: '2px 0 0' }}>
                {modalSubtitle}
              </p>
            </div>
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
                display: 'flex',
                color: '#9CA3AF',
                borderRadius: 4,
              }}
            >
              <MdClose size={18} />
            </button>
          </div>

          {/* Two-panel body */}
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <div style={{ display: 'flex', minHeight: 260 }}>

              {/* Left panel: Cohort Info */}
              <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <SectionLabel>Cohort Info</SectionLabel>

                {isView && editTarget?.id && (
                  <FormField label="Cohort ID">
                    <div style={{ ...inputStyle(true), display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: 12 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{editTarget.id}</span>
                      <button
                        onClick={(e) => {
                          const btn = e.currentTarget;
                          const copyIcon = btn.querySelector('.copy-icon');
                          const checkIcon = btn.querySelector('.check-icon');
                          navigator.clipboard.writeText(editTarget.id).catch(() => {
                            const el = document.createElement('textarea');
                            el.value = editTarget.id;
                            document.body.appendChild(el);
                            el.select();
                            document.execCommand('copy');
                            document.body.removeChild(el);
                          });
                          if (copyIcon) copyIcon.style.display = 'none';
                          if (checkIcon) checkIcon.style.display = 'inline-flex';
                          setTimeout(() => {
                            if (copyIcon) copyIcon.style.display = 'inline-flex';
                            if (checkIcon) checkIcon.style.display = 'none';
                          }, 1500);
                        }}
                        title="Copy cohort ID"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 6px', display: 'inline-flex', alignItems: 'center', color: '#9CA3AF', flexShrink: 0 }}
                      >
                        <span className="copy-icon" style={{ display: 'inline-flex', alignItems: 'center' }}><MdContentCopy size={13} /></span>
                        <span className="check-icon" style={{ display: 'none', alignItems: 'center', color: '#10B981' }}><MdCheck size={13} /></span>
                      </button>
                    </div>
                  </FormField>
                )}

                <FormField label="Cohort Name" required={!isView}>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. DS Batch 2025 Q1"
                    disabled={isView || !!editTarget}
                    style={inputStyle(isView || !!editTarget)}
                  />
                </FormField>

                <FormField label="Course Type" required={!isView}>
                  <div style={{ position: 'relative' }}>
                    <select
                      name="courseType"
                      value={form.courseType}
                      onChange={(e) => setForm((prev) => ({ ...prev, courseType: e.target.value }))}
                      disabled={isView || !!editTarget}
                      style={{
                        ...inputStyle(isView || !!editTarget),
                        appearance: 'none',
                        paddingRight: 32,
                        cursor: isView || editTarget ? 'default' : 'pointer',
                      }}
                    >
                      {Object.entries(COURSE_TYPE_NAMES_MAPPING).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9CA3AF', fontSize: 12 }}>▾</span>
                  </div>
                </FormField>
              </div>

              {/* Vertical divider */}
              <div style={{ width: 1, backgroundColor: '#F3F4F6', flexShrink: 0 }} />

              {/* Right panel: Dates */}
              <div style={{ flex: 1, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, backgroundColor: '#FAFAFA' }}>
                <SectionLabel>Dates</SectionLabel>

                <FormField label="Start Date">
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="dd/MM/yyyy"
                    placeholder="DD/MM/YYYY"
                    value={form.startDate}
                    onChange={(date) => setForm((prev) => ({ ...prev, startDate: date }))}
                    inputVariant="outlined"
                    size="small"
                    disabled={isView}
                    InputProps={{ style: { fontSize: 13, fontFamily: "'Gellix','Inter',sans-serif" } }}
                    style={{ width: '100%' }}
                  />
                </FormField>

                <FormField label="Class Start Date">
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="dd/MM/yyyy"
                    placeholder="DD/MM/YYYY"
                    value={form.classStartDate}
                    onChange={(date) => setForm((prev) => ({ ...prev, classStartDate: date }))}
                    inputVariant="outlined"
                    size="small"
                    disabled={isView}
                    InputProps={{ style: { fontSize: 13, fontFamily: "'Gellix','Inter',sans-serif" } }}
                    style={{ width: '100%' }}
                  />
                </FormField>

                <FormField label="Schedule Date">
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="dd/MM/yyyy"
                    placeholder="DD/MM/YYYY"
                    value={form.scheduleDate}
                    onChange={(date) => setForm((prev) => ({ ...prev, scheduleDate: date }))}
                    inputVariant="outlined"
                    size="small"
                    disabled={isView}
                    InputProps={{ style: { fontSize: 13, fontFamily: "'Gellix','Inter',sans-serif" } }}
                    style={{ width: '100%' }}
                  />
                </FormField>
              </div>
            </div>

            {/* Footer */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '14px 24px',
                borderTop: '1px solid #F3F4F6',
                backgroundColor: '#fff',
              }}
            >
              {isView ? (
                <button
                  onClick={handleClose}
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: '#4F46E5',
                    border: 'none',
                    borderRadius: 4,
                    padding: '8px 20px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                  }}
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSubmit}
                    disabled={submitting || !form.name || !form.courseType}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#fff',
                      backgroundColor: '#4F46E5',
                      border: 'none',
                      borderRadius: 4,
                      padding: '8px 20px',
                      cursor: submitting || !form.name ? 'not-allowed' : 'pointer',
                      opacity: submitting || !form.name ? 0.6 : 1,
                      boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                      transition: 'opacity 150ms',
                    }}
                  >
                    {submitting ? 'Saving…' : editTarget ? 'Update Cohort' : 'Create Cohort'}
                  </button>
                  <button
                    onClick={handleReset}
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#6B7280',
                      backgroundColor: 'transparent',
                      border: '1px solid #E5E7EB',
                      borderRadius: 4,
                      padding: '8px 16px',
                      cursor: 'pointer',
                    }}
                  >
                    Reset
                  </button>
                </>
              )}
            </div>
          </MuiPickersUtilsProvider>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        color: '#9CA3AF',
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        margin: '0 0 12px 0',
      }}
    >
      {children}
    </p>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: '#374151', display: 'flex', gap: 3 }}>
        {label}
        {required && <span style={{ color: '#EF4444', lineHeight: 1 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function inputStyle(disabled) {
  return {
    width: '100%',
    height: 36,
    backgroundColor: disabled ? '#F9FAFB' : '#fff',
    border: '1px solid #E5E7EB',
    borderRadius: 4,
    padding: '0 10px',
    fontSize: 13,
    color: disabled ? '#9CA3AF' : '#111827',
    boxSizing: 'border-box',
    fontFamily: "'Gellix', 'Inter', sans-serif",
    transition: 'border-color 150ms',
  };
}
